import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Skeleton } from '@mui/material';
import { ReusableTransformType } from '@nexla/@types';
import CardDetailView from 'js/components/elements/cards/CardDetailView';
import {
  ReusableTransformDetailHeader,
  useGetDataToReusableTransformDetailHeader,
} from 'js/components/reusableTransforms/cards/ReusableTransformDetailHeader';
import { deselectCard, selectCard } from 'js/actions';
import StyledFixedPanel from 'new/components/StyledFixedPanel';
import { ReusableTransformUtil } from 'new/utils/ReusableTransform/argumentValues';
import AttributeTransformItem from '../AttributeTransformItem/AttributeTransformItem';
import { convertTransformToCode, decodeTranformCode } from 'js/lib/TransformsUtils';
import './AttributeTransformList.scss';
import useAppDispatch from 'js/hooks/useAppDispatch';

type Props = {
  currentName?: string;
  data: ReusableTransformType[];
  transformBeingEditedID?: number;
  isFetchingReusableTransforms: boolean;
};

const ListSkeleton = () => (
  <>
    {new Array(11).fill(0).map((i, n) => (
      <li className="skeleton" key={n}>
        <div className="item-label">
          <div className="icon-box" />
          <Skeleton animation="wave" variant="rounded" className="title" width={160} />
        </div>
        <div className="chips">
          <Skeleton animation="wave" variant="rounded" className="chips" width={80} />
        </div>
      </li>
    ))}
  </>
);

function AttributeTransformList(props: Props) {
  const { data = [], currentName, isFetchingReusableTransforms } = props;

  const dispatch = useAppDispatch();
  const [resource, setResource] = useState(null);
  const { aclQueryMode = '' } = useSelector((state: any) => state.app.listing);
  const getDataCallback = useGetDataToReusableTransformDetailHeader(data, aclQueryMode);

  if (!Array.isArray(data)) {
    throw {
      message: 'Data property must be an array',
      componentName: 'AttributeTransformList',
    };
  }

  if (data.length === 0) return null;

  const onCloseSelectedCard = async () => {
    await dispatch(deselectCard());
    setResource(null);
  };

  const onOpenSelectedCard = async (card: ReusableTransformType) => {
    await dispatch(selectCard(card.id));
    // transform entities do not have decoded data in redux, decoding done here based on tx type
    let cardData;
    if (!card?.decoded && card.output_type === 'attribute') {
      const decoded = decodeTranformCode(card?.code, card?.code_encoding);
      cardData = { ...card, decoded };
    } else if (!card?.decoded && card.output_type === 'record') {
      const { code } = convertTransformToCode(card?.code);
      cardData = { ...card, decoded: code };
    } else {
      cardData = { ...card };
    }
    setResource({
      data: cardData,
      tabs: ReusableTransformUtil.generateTabs(data),
      detailHeaderContent: React.createElement(ReusableTransformDetailHeader, {
        ...getDataCallback(data.findIndex((d) => d.id === card.id)),
        adminAccess: false,
        hideActions: true,
        showActionsContainer: false,
        onCloseDrawer: onCloseSelectedCard,
      }),
    });
  };

  const currentAttributeTransform = currentName ? (
    <AttributeTransformItem
      key={'item_transform_list_current'}
      itemKey={'item_transform_list_current'}
      itemData={
        props?.transformBeingEditedID
          ? data.find((i) => i.id === props?.transformBeingEditedID)
          : { name: currentName }
      }
      onSelect={onOpenSelectedCard}
    />
  ) : null;

  return (
    <>
      <div className="attribute-transform-component">
        <ul aria-label="attribute-transform-list">
          {currentAttributeTransform}
          {isFetchingReusableTransforms ? (
            <ListSkeleton />
          ) : (
            data.map((item, idx) => {
              if (props?.transformBeingEditedID === item?.id) return null;
              const itemKey = 'item_transform_list_' + idx;
              return (
                <AttributeTransformItem
                  key={itemKey}
                  itemKey={itemKey}
                  itemData={item}
                  onSelect={onOpenSelectedCard}
                />
              );
            })
          )}
        </ul>
      </div>
      {resource ? (
        <div className="attribute-transform-overlay-modal">
          <div className="modal-fader" onClick={onCloseSelectedCard} />
          <StyledFixedPanel>
            <CardDetailView tabs={[]} noHeaderPadding aclQueryMode={aclQueryMode} {...resource} />
          </StyledFixedPanel>
        </div>
      ) : null}
    </>
  );
}

export default AttributeTransformList;
